
import React, { useState } from 'react';
import { Mail, Sparkles, Check, Loader2 } from 'lucide-react';
import { subscribeUser } from '../services/geminiService';

const Newsletter: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    
    setStatus('loading');
    try {
      await subscribeUser(formData.name, formData.email);
      setStatus('success');
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <div id="newsletter-section" className="relative overflow-hidden bg-slate-950 p-8 md:p-12 rounded-sm shadow-2xl group">
      {/* Decorative Corner */}
      <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-amber-500/20 group-hover:border-amber-500/50 transition-all duration-700" />
      <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-amber-500/20 group-hover:border-amber-500/50 transition-all duration-700" />

      {status === 'success' ? (
        <div className="animate-fade-in py-10 text-center">
          <div className="w-20 h-20 bg-amber-500 text-slate-950 rounded-full flex items-center justify-center mx-auto mb-8 shadow-[0_0_30px_rgba(245,158,11,0.4)]">
            <Check size={40} />
          </div>
          <h3 className="text-3xl font-serif text-slate-50 mb-4 uppercase tracking-widest">Ritual Complete</h3>
          <p className="text-indigo-200/80 max-w-sm mx-auto leading-relaxed">
            Welcome to the Inner Circle. The heavens have acknowledged your signature. You will receive cosmic updates soon.
          </p>
        </div>
      ) : (
        <div className="text-center">
          <div className="mb-10">
            <div className="flex justify-center mb-6">
              <div className="p-3 bg-amber-500/10 text-amber-500 rounded-full border border-amber-500/20">
                <Sparkles size={32} className="animate-pulse" />
              </div>
            </div>
            <h3 className="text-3xl font-serif text-slate-50 mb-4 uppercase tracking-widest shimmer-text">Join the Inner Circle</h3>
            <p className="text-indigo-200/70 text-sm leading-relaxed font-light max-w-md mx-auto">
              Receive celestial insights, exclusive reading deals, and monthly lunar updates directly from Allie's desk.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 max-w-sm mx-auto">
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Full Name"
                  className="w-full bg-slate-900 border border-indigo-900/50 p-4 text-indigo-50 placeholder:text-indigo-600/50 focus:outline-none focus:border-amber-500/50 transition-all rounded-sm text-sm"
                />
              </div>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email Address"
                  className="w-full bg-slate-900 border border-indigo-900/50 p-4 text-indigo-50 placeholder:text-indigo-600/50 focus:outline-none focus:border-amber-500/50 transition-all rounded-sm text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-5 bg-amber-500 text-slate-950 font-bold uppercase tracking-[0.2em] text-xs transition-all shadow-[0_0_20px_rgba(245,158,11,0.1)] rounded-sm flex items-center justify-center gap-3 group"
            >
              {status === 'loading' ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  Subscribe to Insights <Mail size={16} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            
            {status === 'error' && (
              <p className="text-rose-500 text-[10px] uppercase font-bold">
                The connection was interrupted. Please try again.
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
};

export default Newsletter;
